/**
 * Workbench partition key (见 03).
 *
 * A source module contributes one opaque segment. The host joins the segments
 * that currently have a value into a single string and broadcasts that string.
 * It never interprets a segment: a workspace id, an account id, or anything
 * else is the contributor's choice, and the host only knows the joined key.
 *
 * **Pull, like `registerScopeLayer`.** `read()` is synchronous because the
 * consumers that will key their own stores (the rail layout, recent searches)
 * live in this process. A sandboxed module cannot register one: the host cannot
 * call a synchronous `read()` across `invoke` / `reply`.
 *
 * Editor tabs are the first consumer: the host keeps one list of open document
 * tabs per key, writes it to storage, and drops every list on `resetEditorScope`.
 * The rail pin table does not read the key yet.
 */

/** Cross-process channel for the current partition key. */
export const PARTITION_CHANNEL = 'workbench-partition';

/**
 * EventBus event emitted when the joined key changes. The payload is the whole
 * key, not a delta.
 *
 * Deliberately **not** in `PLATFORM_PUBLIC_EVENTS`. The key is assembled from
 * module business state (later: which account, which workspace), which a
 * sandboxed surface must not observe.
 */
export const PARTITION_CHANGED_EVENT = 'platform.workbench.partitionChanged';

/**
 * Separates segments inside the joined key. A value that contains it is
 * rejected: the host does not parse the key back apart, so a value carrying
 * the separator could make two different segment sets join to the same string.
 *
 * U+001F cannot appear in a module id (`isUnsafeModuleId` rejects every C0
 * control), so it only has to be kept out of values.
 */
export const PARTITION_SEGMENT_SEPARATOR = '\u001f';

/**
 * Why `value` cannot be a partition segment, or `undefined` when it can.
 * An empty string is not a problem here — the host treats it as "this segment
 * is absent" rather than as an illegal value.
 */
export function partitionValueProblem(value: string): string | undefined {
  if (value.includes(PARTITION_SEGMENT_SEPARATOR)) {
    return `value contains U+${PARTITION_SEGMENT_SEPARATOR.charCodeAt(0).toString(16).padStart(4, '0').toUpperCase()}, the partition separator`;
  }
  return undefined;
}

/** One module's contribution to the joined key. */
export interface PartitionSegment {
  /**
   * Join order. Lower comes first. Ties break by module id, so the key is
   * stable when two segments share an order. This is not a priority: every
   * present segment is part of the key, and a higher order does not replace a
   * lower one.
   */
  readonly order: number;
  /**
   * The current opaque value, or `undefined` / `''` when this segment should
   * not appear in the key. A non-empty value must not contain
   * {@link PARTITION_SEGMENT_SEPARATOR}; that throws.
   */
  read(): string | undefined;
  /**
   * Fired when `read()` may return something else. The host re-reads every
   * segment and broadcasts only when the joined key actually changes.
   */
  onDidChange?(listener: () => void): () => void;
}

/** Payload of {@link PARTITION_CHANGED_EVENT}. */
export interface PartitionSnapshot {
  /**
   * The joined key. `''` when no segment currently contributes a value — there
   * is no anonymous bucket to write into.
   */
  readonly key: string;
}

/** One editor instance tab remembered for a partition key. Identity only. */
export interface EditorPartitionTab {
  readonly viewId: string;
  readonly instanceKey: string;
}

/**
 * The editor strip for one partition key. Not written for the empty key.
 * `resetEditorScope` discards every key. The host persists the rest, so a
 * restart restores the document tabs for whichever key is current. It does
 * not restore the rail singleton.
 */
export interface EditorPartitionSnapshot {
  /**
   * Left null. Older buckets stored the rail singleton here (`contacts.main`,
   * `im.sessions`, …). Restore ignores the field: applying it reopened that
   * launcher on every start. The rail view is the live session, or
   * `workbench.startupEditorView` when the session has not chosen one.
   */
  readonly activeViewId: string | null;
  readonly tabs: readonly EditorPartitionTab[];
  /**
   * `viewId::instanceKey` of the focused instance tab. `null` when the anchor
   * is in front.
   */
  readonly focusedInstanceId: string | null;
}

export const EMPTY_EDITOR_PARTITION: EditorPartitionSnapshot = {
  activeViewId: null,
  tabs: [],
  focusedInstanceId: null,
};

/**
 * What {@link IPartitionService.getSnapshot} returns. `editorClock` advances
 * when `resetEditorScope` drops every editor-tab bucket. A `setEditorTabs`
 * that still carries an older clock is ignored, so a save that was already in
 * flight cannot write the cleared strip back.
 */
export interface PartitionView extends PartitionSnapshot {
  readonly editorClock: number;
}

/**
 * Trusted-renderer RPC for the current partition key and its editor tabs.
 *
 * Read at boot and on every reconnect rather than relying on the broadcast
 * alone: a module can publish its segment before any window exists.
 */
export interface IPartitionService {
  getSnapshot(): Promise<PartitionView>;
  getEditorTabs(key: string): Promise<EditorPartitionSnapshot>;
  setEditorTabs(
    key: string,
    snapshot: EditorPartitionSnapshot,
    editorClock: number,
  ): Promise<void>;
}
